import { Toaster } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Layout from "@/components/Layout";
import Trucks from "@/pages/Trucks";
import TruckEntry from "@/pages/TruckEntry";
import Parties from "@/pages/Parties";
import Transporters from "@/pages/Transporters";
import Materials from "@/pages/Materials";
import Bills from "@/pages/Bills";
import BillCreate from "@/pages/BillCreate";
import BillDetail from "@/pages/BillDetail";
import PaymentsReceived from "@/pages/PaymentsReceived";
import PaymentsMade from "@/pages/PaymentsMade";
import PartyLedger from "@/pages/PartyLedger";
import TransporterLedger from "@/pages/TransporterLedger";
import MaterialLedger from "@/pages/MaterialLedger";
import PartyLedgers from "@/pages/PartyLedgers";
import TransporterLedgers from "@/pages/TransporterLedgers";
import Settings from "@/pages/Settings";
import AccountsSheet from "@/pages/AccountsSheet";
import POs from "@/pages/POs";
import BulkImport from "@/pages/BulkImport";
import Gstr1 from "@/pages/Gstr1";
import AuditLog from "@/pages/AuditLog";
import TransporterSettlement from "@/pages/TransporterSettlement";
import PartyStatement from "@/pages/PartyStatement";
import BillRegister from "@/pages/BillRegister";
import Users from "@/pages/Users";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="font-serif text-2xl text-brand-forest tracking-tight">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force password change on first login (admin-provisioned accounts).
  if (user.must_change_password && !location.pathname.startsWith("/settings")) {
    return <Navigate to="/settings" replace />;
  }
  return children;
}

function AdminOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="trucks" element={<Trucks />} />
            <Route path="trucks/new" element={<TruckEntry />} />
            <Route path="trucks/:id/edit" element={<TruckEntry />} />
            <Route path="bills" element={<Bills />} />
            <Route path="bills/new" element={<BillCreate />} />
            <Route path="bills/:id" element={<BillDetail />} />
            <Route path="parties" element={<Parties />} />
            <Route path="transporters" element={<Transporters />} />
            <Route path="materials" element={<Materials />} />
            <Route path="payments-received" element={<PaymentsReceived />} />
            <Route path="payments-made" element={<PaymentsMade />} />
            <Route path="party-ledgers" element={<PartyLedgers />} />
            <Route path="transporter-ledgers" element={<TransporterLedgers />} />
            <Route path="accounts-sheet" element={<AccountsSheet />} />
            <Route path="bill-register" element={<BillRegister />} />
            <Route path="settings" element={<Settings />} />
            <Route path="pos" element={<POs />} />
            <Route path="bulk-import" element={<BulkImport />} />
            <Route path="audit-log" element={<AdminOnlyRoute><AuditLog /></AdminOnlyRoute>} />
            <Route path="users" element={<AdminOnlyRoute><Users /></AdminOnlyRoute>} />
            <Route path="gstr1" element={<Gstr1 />} />
            <Route path="ledger/party/:id" element={<PartyLedger />} />
            <Route path="ledger/transporter/:id" element={<TransporterLedger />} />
            <Route path="ledger/material/:id" element={<MaterialLedger />} />
            <Route path="transporter-settlement/:id" element={<TransporterSettlement />} />
            <Route path="party-statement/:id" element={<PartyStatement />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
